import { useEffect, useMemo, useState } from "react";
import { Check, X } from "lucide-react";
import { cn } from "./ui";
import { notificationKinds, type AppNotification } from "./notifications-meta";
import { t } from "@/lib/i18n";

/* Панель уведомлений: выпадает из колокольчика в шапке окна.
   Вкладки «Новый / Архив / Все», прочтение по клику, архивирование по одному и все сразу,
   плюс карточка разрешения браузерных уведомлений — как в оригинальном интерфейсе. */

type Tab = "new" | "archive" | "all";

const TABS: { id: Tab; label: string }[] = [
  { id: "new", label: t("Новый") },
  { id: "archive", label: t("Архив") },
  { id: "all", label: t("Все") },
];

type Props = {
  open: boolean;
  items: AppNotification[];
  prompt: boolean;
  onClose: () => void;
  onRead: (id: string) => void;
  onArchive: (id: string) => void;
  onRestore: (id: string) => void;
  onArchiveAll: () => void;
  onOpen: (item: AppNotification) => void;
  onLater: () => void;
  onEnableBrowser: () => void;
  onOpenSettings: () => void;
};

type Group = { name: string; list: AppNotification[] };

export default function NotificationsPanel({
  open,
  items,
  prompt,
  onClose,
  onRead,
  onArchive,
  onRestore,
  onArchiveAll,
  onOpen,
  onLater,
  onEnableBrowser,
  onOpenSettings,
}: Props) {
  const [tab, setTab] = useState<Tab>("new");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const unread = useMemo(() => items.filter((n) => !n.read && !n.archived).length, [items]);
  const inboxCount = useMemo(() => items.filter((n) => !n.archived).length, [items]);
  const archivedCount = useMemo(() => items.filter((n) => n.archived).length, [items]);

  const visible = useMemo(() => {
    if (tab === "new") return items.filter((n) => !n.archived);
    if (tab === "archive") return items.filter((n) => n.archived);
    return items;
  }, [items, tab]);

  const groups = useMemo<Group[]>(() => {
    const order: string[] = [];
    const map = new Map<string, AppNotification[]>();
    visible.forEach((n) => {
      if (!map.has(n.group)) {
        map.set(n.group, []);
        order.push(n.group);
      }
      map.get(n.group)?.push(n);
    });
    return order.map((name) => ({ name, list: map.get(name) ?? [] }));
  }, [visible]);

  if (!open) return null;

  const activate = (item: AppNotification) => {
    onRead(item.id);
    onOpen(item);
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={t("Уведомления")}
      className="absolute end-0 top-[calc(100%+0.45rem)] z-[60] flex w-[min(23rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-card border border-border bg-card text-card-foreground shadow-lift"
    >
      <div className="flex items-center gap-2 border-b border-border px-3.5 py-2.5">
        <h4 className="min-w-0 flex-1 truncate text-[13px] font-semibold">{t("Уведомления")}</h4>
        <button
          type="button"
          onClick={onArchiveAll}
          disabled={inboxCount === 0}
          className={cn(
            "inline-flex h-6 flex-none items-center whitespace-nowrap rounded-btn px-1.5 text-[11px] font-semibold transition",
            inboxCount === 0 ? "cursor-not-allowed text-muted-foreground/50" : "cursor-pointer text-primary hover:bg-primary/10",
          )}
        >
          {t("Архивировать все")}
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("Закрыть уведомления")}
          className="grid size-6 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      </div>

      <div className="flex items-center gap-1 border-b border-border px-2.5 pt-1.5">
        {TABS.map((tb) => {
          const active = tab === tb.id;
          const count = tb.id === "new" ? unread : tb.id === "archive" ? archivedCount : items.length;
          return (
            <button
              key={tb.id}
              type="button"
              onClick={() => setTab(tb.id)}
              aria-pressed={active}
              className={cn(
                "relative -mb-px inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap border-b-2 px-2.5 pb-2 pt-1 text-[12px] font-semibold transition",
                active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {tb.label}
              {count > 0 ? (
                <span
                  className={cn(
                    "inline-flex min-w-[1rem] items-center justify-center rounded-full px-1 text-[9.5px] font-bold leading-[1.4] tabular-nums",
                    active ? "bg-primary text-primary-foreground" : "bg-foreground/10 text-muted-foreground",
                  )}
                >
                  {count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="max-h-[min(46vh,21rem)] overflow-y-auto">
        {prompt && tab === "new" ? (
          <div className="m-2.5 rounded-btn border border-border bg-background/70 p-2.5">
            <div className="flex items-start gap-2.5">
              <span className="grid size-8 flex-none place-items-center rounded-btn bg-foreground text-background" aria-hidden>
                <Check className="size-4" strokeWidth={3} />
              </span>
              <p className="min-w-0 flex-1 text-[11.5px] leading-relaxed text-foreground/85">
                {t("Включите уведомления браузера об обновлениях. Вы можете изменить их в любое время в настройках уведомлений.")}
              </p>
            </div>
            <div className="mt-2.5 flex items-center justify-end gap-1.5">
              <button
                type="button"
                onClick={onLater}
                className="inline-flex h-7 cursor-pointer items-center whitespace-nowrap rounded-btn px-2.5 text-[11.5px] font-semibold text-primary transition hover:bg-primary/10"
              >
                {t("Позже")}
              </button>
              <button
                type="button"
                onClick={onEnableBrowser}
                className="inline-flex h-7 cursor-pointer items-center whitespace-nowrap rounded-btn bg-primary px-3 text-[11.5px] font-semibold text-primary-foreground transition hover:brightness-110"
              >
                {t("Включить сейчас")}
              </button>
            </div>
          </div>
        ) : null}

        {visible.length === 0 ? (
          <div className="flex flex-col items-center gap-1.5 px-5 py-10 text-center">
            <span className="grid size-12 place-items-center rounded-full border border-border bg-background/60" aria-hidden>
              <i
                className={cn(
                  tab === "archive" ? "ri-archive-line" : "ri-notification-off-line",
                  "text-xl leading-none text-muted-foreground",
                )}
              />
            </span>
            <p className="mt-1 text-[12.5px] font-semibold">
              {tab === "new" ? t("Новых уведомлений нет") : tab === "archive" ? t("Архив пуст") : t("Уведомлений пока нет")}
            </p>
            <p className="max-w-[16rem] text-[11px] leading-relaxed text-muted-foreground">
              {tab === "new"
                ? t("Мы сообщим, когда экспорт будет готов, рассылка уйдёт или клиент долго ждёт ответа.")
                : tab === "archive"
                  ? t("Сюда попадают уведомления, которые вы убрали из новых.")
                  : t("Здесь появятся все события рабочего пространства.")}
            </p>
          </div>
        ) : (
          groups.map((g) => (
            <div key={g.name} className="pb-1">
              <p className="px-3.5 pb-1 pt-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{t(g.name)}</p>
              <ul>
                {g.list.map((n) => {
                  const meta = notificationKinds[n.kind];
                  return (
                    <li key={n.id} className="group relative border-b border-border/50 last:border-b-0">
                      <div className="flex items-start gap-2.5 px-3.5 py-2.5 transition hover:bg-muted/50">
                        <span
                          className={cn(
                            "mt-0.5 grid size-8 flex-none place-items-center rounded-full text-[15px] leading-none",
                            n.read ? "bg-foreground/[.06] text-muted-foreground" : "bg-primary/12 text-primary",
                          )}
                          aria-hidden
                        >
                          <i className={meta.icon} />
                        </span>

                        <div className="min-w-0 flex-1">
                          <button
                            type="button"
                            onClick={() => activate(n)}
                            className="flex w-full cursor-pointer items-start gap-1.5 text-start"
                          >
                            <span className={cn("min-w-0 flex-1 text-[12px] leading-snug", n.read ? "font-medium text-foreground/85" : "font-semibold")}>
                              {n.title}
                            </span>
                            {n.read ? null : <span className="mt-1.5 size-1.5 flex-none rounded-full bg-primary" aria-label={t("Не прочитано")} />}
                          </button>

                          <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[10px] text-muted-foreground">
                            <span>{n.time}</span>
                            <span aria-hidden>·</span>
                            <span>{meta.label}</span>
                          </p>

                          <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{n.text}</p>

                          {n.action ? (
                            <button
                              type="button"
                              onClick={() => activate(n)}
                              className="mt-1 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-[11px] font-semibold text-primary underline decoration-dotted underline-offset-2 transition hover:brightness-110"
                            >
                              {n.action}
                              <i className="ri-arrow-right-up-line text-[11px] leading-none" aria-hidden />
                            </button>
                          ) : null}
                        </div>

                        <div className="flex flex-none flex-col gap-0.5 opacity-50 transition focus-within:opacity-100 group-hover:opacity-100">
                          {n.read ? null : (
                            <button
                              type="button"
                              onClick={() => onRead(n.id)}
                              title={t("Отметить прочитанным")}
                              aria-label={t("Отметить «{title}» прочитанным", { title: n.title })}
                              className="grid size-6 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
                            >
                              <i className="ri-check-double-line text-[13px] leading-none" aria-hidden />
                            </button>
                          )}
                          {n.archived ? (
                            <button
                              type="button"
                              onClick={() => onRestore(n.id)}
                              title={t("Вернуть в новые")}
                              aria-label={t("Вернуть «{title}» в новые", { title: n.title })}
                              className="grid size-6 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
                            >
                              <i className="ri-inbox-unarchive-line text-[13px] leading-none" aria-hidden />
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => onArchive(n.id)}
                              title={t("Архивировать")}
                              aria-label={t("Архивировать «{title}»", { title: n.title })}
                              className="grid size-6 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
                            >
                              <i className="ri-archive-line text-[13px] leading-none" aria-hidden />
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-border bg-background/60 px-3.5 py-2">
        <button
          type="button"
          onClick={onOpenSettings}
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold text-foreground/75 transition hover:text-primary"
        >
          <i className="ri-settings-3-line text-[13px] leading-none" aria-hidden />
          {t("Настройки уведомлений")}
        </button>
        <span className="ms-auto whitespace-nowrap text-[10px] text-muted-foreground">
          {t("Непрочитанных: {n}", { n: unread })}
        </span>
      </div>
    </div>
  );
}